import { motion, useMotionValue, useTransform } from "framer-motion";
import { MaskedLine } from "./primitives";
import { CtaButton, TrustRow } from "./Cta";

export const Hero = () => {
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rotateX = useTransform(my, [-0.5, 0.5], [5, -5]);
  const rotateY = useTransform(mx, [-0.5, 0.5], [-7, 7]);

  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  return (
    <header
      data-testid="hero-section"
      onMouseMove={onMove}
      className="relative overflow-hidden"
    >
      <div className="hero-grid absolute inset-0" />
      <div className="absolute -top-44 left-1/2 h-[480px] w-[820px] -translate-x-1/2 rounded-full bg-cyan-500/10 blur-[130px]" />
      <div className="relative mx-auto grid max-w-7xl gap-14 px-6 pb-24 pt-32 sm:pt-36 lg:grid-cols-[1.08fr_0.92fr] lg:items-center lg:pb-28 lg:pt-44">
        <div>
          <motion.div
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="mb-7 inline-flex items-center gap-3 rounded-full border border-cyan-400/30 bg-cyan-400/5 px-4 py-2"
          >
            <span className="status-pulse h-1.5 w-1.5 rounded-full bg-cyan-300" />
            <span className="font-tech text-[11px] uppercase tracking-[0.28em] text-cyan-200">
              Especialización práctica · Mindray BA-88A
            </span>
          </motion.div>

          <h1
            data-testid="hero-headline"
            className="font-display text-4xl font-extrabold leading-[1.06] tracking-tight text-slate-50 sm:text-5xl lg:text-6xl"
          >
            <MaskedLine delay={0.15}>Domina el diagnóstico,</MaskedLine>
            <MaskedLine delay={0.28}>
              reparación y calibración del <span className="text-cyan-300">Mindray BA-88A</span>
            </MaskedLine>
            <MaskedLine delay={0.41}>
              en menos de <span className="text-amber-400">3 meses</span> y multiplica
            </MaskedLine>
            <MaskedLine delay={0.54}>tus tarifas de servicio técnico</MaskedLine>
          </h1>

          <motion.h2
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.85 }}
            className="mt-7 max-w-xl text-base leading-relaxed text-slate-400 md:text-lg"
          >
            El programa práctico y paso a paso para ingenieros biomédicos, electrónicos y
            técnicos que quieren dejar atrás los mantenimientos baratos de $20 dólares y
            convertirse en el especialista indispensable de los laboratorios clínicos.
          </motion.h2>

          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.05 }}
            className="mt-10"
          >
            <CtaButton testId="hero-cta-button">
              Quiero inscribirme por $297 USD y asegurar mis bonos
            </CtaButton>
            <TrustRow className="mt-7" />
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, scale: 0.94 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.2, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
          className="relative [perspective:1100px]"
        >
          <motion.div
            data-testid="hero-photo-frame"
            style={{ rotateX, rotateY, transformStyle: "preserve-3d" }}
            className="relative"
          >
            <div className="absolute -inset-2 rounded-[2rem] bg-gradient-to-br from-cyan-400/25 via-transparent to-amber-400/20 blur-lg" />
            <div className="relative overflow-hidden rounded-3xl border border-white/10 shadow-[0_40px_90px_rgba(0,0,0,0.55)]">
              <img
                src="/images/ba88a.jpg"
                alt="Analizador de química clínica Mindray BA-88A en taller"
                className="aspect-[4/3] w-full object-cover"
                data-testid="hero-device-photo"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#070B12]/85 via-transparent to-[#070B12]/15" />
              <div className="absolute left-5 top-5 rounded-full border border-cyan-400/40 bg-[#070B12]/70 px-4 py-1.5 backdrop-blur">
                <span className="font-tech text-[10px] tracking-[0.3em] text-cyan-200">
                  EQUIPO REAL · TALLER
                </span>
              </div>
              <div className="absolute bottom-5 left-5 right-5 flex items-end justify-between gap-4">
                <div>
                  <div className="font-tech text-[10px] tracking-[0.3em] text-slate-400">
                    ANALIZADOR SEMIAUTOMÁTICO
                  </div>
                  <div className="mt-1 font-display text-xl font-bold text-slate-50">
                    Mindray BA-88A
                  </div>
                </div>
                <div className="rounded-xl border border-cyan-400/30 bg-[#0B1322]/85 px-3.5 py-2 backdrop-blur">
                  <span className="font-tech text-[9px] tracking-[0.25em] text-cyan-300">ABSORBANCIA</span>
                  <div className="font-tech text-lg font-semibold leading-none text-slate-50">0.847 AU</div>
                </div>
              </div>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </header>
  );
};
